/**
 * SetTemporaryPinModal — bloqueante, no se puede cerrar con ESC ni clic en
 * backdrop ni botón Cancelar. Se muestra cuando el backend señala
 * `mustChangePin: true` o `me.pin.forceChange === true` (ver lib/api.ts
 * interceptor que emite el evento global `synapta:must-change-pin`).
 *
 * Contrato (ARES-CORE-001):
 *   POST /api/v3/auth/pin/change body { currentPin?, newPin }
 *   - currentPin obligatorio si props.requireCurrentPin (variante temporal)
 *   - currentPin opcional / vacío si !requireCurrentPin (variante primer-PIN)
 *   - newPin: 4-8 dígitos, distinto del actual
 *   - 200 → backend re-hashea + setea force_change=0
 *   - 401/403 → PIN actual incorrecto
 *   - 423 → usuario bloqueado por intentos
 *   - 400 → PIN inválido (no cumple regex / igual al actual)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { changePin } from "@/lib/api";
import type { AxiosError } from "axios";

const PIN_RE = /^\d{4,8}$/;

export interface SetTemporaryPinModalProps {
  /** Si true, pide el PIN actual (cambio de PIN temporal). Si false, es el
   * primer PIN del usuario (no se ha seteado todavía). */
  requireCurrentPin?: boolean;
  /** Llamado tras un cambio exitoso. */
  onSuccess: () => void;
}

type SubmitError =
  | { kind: "validation"; msg: string }
  | { kind: "wrong_current"; msg: string }
  | { kind: "locked"; msg: string }
  | { kind: "invalid"; msg: string }
  | { kind: "network"; msg: string };

function mapError(e: unknown): SubmitError {
  const ax = e as AxiosError<{ message?: string; error?: string }>;
  const status = ax?.response?.status;
  const serverMsg = ax?.response?.data?.message || ax?.response?.data?.error;
  if (status === 401 || status === 403) {
    return { kind: "wrong_current", msg: "El PIN actual no es correcto." };
  }
  if (status === 423) {
    return {
      kind: "locked",
      msg: serverMsg ||
        "Usuario bloqueado por demasiados intentos. Espera unos minutos.",
    };
  }
  if (status === 400) {
    return {
      kind: "invalid",
      msg: serverMsg ||
        "El PIN nuevo no es válido (debe ser de 4 a 8 dígitos y distinto del actual).",
    };
  }
  return {
    kind: "network",
    msg: "No pudimos cambiar el PIN. Verifica tu conexión e inténtalo de nuevo.",
  };
}

export function SetTemporaryPinModal({
  requireCurrentPin = true,
  onSuccess,
}: SetTemporaryPinModalProps): JSX.Element {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<SubmitError | null>(null);

  const firstFieldRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap + bloqueo de ESC + foco inicial.
  useEffect(() => {
    firstFieldRef.current?.focus();
    const stopEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'input:not([disabled]), button:not([disabled])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", stopEsc, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", stopEsc, true);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const clientError = useMemo<SubmitError | null>(() => {
    if (!newPin) return null;
    if (!PIN_RE.test(newPin)) {
      return {
        kind: "validation",
        msg: "El PIN nuevo debe ser de 4 a 8 dígitos.",
      };
    }
    if (confirmPin && confirmPin !== newPin) {
      return {
        kind: "validation",
        msg: "El PIN nuevo y la confirmación no coinciden.",
      };
    }
    if (requireCurrentPin && currentPin && currentPin === newPin) {
      return {
        kind: "validation",
        msg: "El PIN nuevo debe ser distinto del actual.",
      };
    }
    return null;
  }, [newPin, confirmPin, currentPin, requireCurrentPin]);

  const canSubmit =
    !submitting &&
    !clientError &&
    PIN_RE.test(newPin) &&
    newPin === confirmPin &&
    (!requireCurrentPin || PIN_RE.test(currentPin));

  const submit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!canSubmit) return;
      setSubmitting(true);
      setErr(null);
      try {
        await changePin({
          currentPin: requireCurrentPin ? currentPin : undefined,
          newPin,
        });
        onSuccess();
      } catch (caught) {
        setErr(mapError(caught));
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, currentPin, newPin, requireCurrentPin, onSuccess],
  );

  const visibleError = err?.msg || clientError?.msg;

  return (
    <div
      className="pin-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div
        ref={dialogRef}
        className="pin-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-modal-title"
        aria-describedby="pin-modal-desc"
      >
        <h2 id="pin-modal-title" className="pin-modal-title">
          {requireCurrentPin
            ? "Define tu PIN definitivo"
            : "Configura tu PIN"}
        </h2>
        <p id="pin-modal-desc" className="pin-modal-desc">
          {requireCurrentPin
            ? "Tu PIN actual es temporal. Por seguridad, cámbialo ahora para continuar."
            : "Tu PIN protege acciones críticas (cancelar CFDI, refacturar, dar de alta usuarios). Defínelo para continuar."}
        </p>

        <form onSubmit={submit} className="pin-modal-form" noValidate>
          {requireCurrentPin && (
            <label className="pin-modal-field">
              <span>PIN actual</span>
              <input
                ref={firstFieldRef}
                type="password"
                inputMode="numeric"
                autoComplete="current-password"
                pattern="\d{4,8}"
                minLength={4}
                maxLength={8}
                value={currentPin}
                onChange={(e) =>
                  setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 8))
                }
                aria-invalid={err?.kind === "wrong_current"}
                required
              />
            </label>
          )}

          <label className="pin-modal-field">
            <span>PIN nuevo (4 a 8 dígitos)</span>
            <input
              ref={requireCurrentPin ? undefined : firstFieldRef}
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              pattern="\d{4,8}"
              minLength={4}
              maxLength={8}
              value={newPin}
              onChange={(e) =>
                setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              aria-invalid={
                err?.kind === "invalid" ||
                clientError?.kind === "validation"
              }
              required
            />
          </label>

          <label className="pin-modal-field">
            <span>Confirma el PIN nuevo</span>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              pattern="\d{4,8}"
              minLength={4}
              maxLength={8}
              value={confirmPin}
              onChange={(e) =>
                setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              aria-invalid={
                clientError?.kind === "validation" && !!confirmPin
              }
              required
            />
          </label>

          {visibleError && (
            <p className="pin-modal-error" role="alert">
              {visibleError}
            </p>
          )}

          <button
            type="submit"
            className="pin-modal-submit"
            disabled={!canSubmit}
          >
            {submitting ? "Guardando…" : "Guardar PIN"}
          </button>

          <p className="pin-modal-help">
            Tip: elige un PIN que recuerdes pero que nadie pueda adivinar
            (evita fechas de cumpleaños, 1234, 0000).
          </p>
        </form>
      </div>
    </div>
  );
}
